import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import cn from "classnames";
import { ElementType, PropsWithChildren } from "react";
import { getDataAutomationGroup, getDataLinkAttributes } from "@/helpers";
import { CloseButton } from "../CloseButton";

export type ModalProps = {
  open: boolean;
  onClose: () => void;
  as?: ElementType;
  autoFocus?: boolean;
  unmount?: boolean;
  role?: "dialog" | "alertdialog";
  closeAnalytics?: { dld?: string; dlt?: string };
  variant?: Variant;
  className?: string;
};

export type Variant = "minimal" | "standard" | "slim" | "exitIntent";

const ANIMATION = cn(
  "translate-y-0 opacity-100 transition-all duration-[350ms] data-closed:translate-y-full data-closed:opacity-0",
);

// This mapping can be used in the future for extending variants of Modal in terms of looks and behavior
const VARIANTS: {
  [key in Variant]: {
    panelWrapper: string;
    panel: string;
    content: string;
  };
} = {
  minimal: {
    panelWrapper: cn(
      "fixed inset-0 flex w-screen items-end justify-center lg:items-center",
    ),
    panel: cn(
      "border-swdc-silver-200 bg-background grid size-[100vh] max-h-[100vh] max-w-[62.125rem] grid-rows-[auto_1fr] overflow-y-auto lg:size-auto",
    ),
    content: cn("bg-background"),
  },
  standard: {
    panelWrapper: cn(
      "fixed inset-0 flex w-screen items-end justify-center lg:items-center",
    ),
    panel: cn(
      "border-swdc-silver-200 bg-swdc-white size-full max-h-[90vh] rounded-t-[1.25rem] border lg:h-auto lg:max-h-160 lg:max-w-308 lg:rounded-[1.25rem] lg:[@media(min-height:980px)]:max-h-220",
    ),
    content: cn(
      "mr-1 overflow-auto py-8 pr-3 pl-4 focus:outline-none focus-visible:outline-none lg:py-20 lg:pr-13 lg:pl-14",
    ),
  },
  slim: {
    panelWrapper: cn(
      "fixed inset-0 flex w-screen items-end justify-center lg:items-center",
    ),
    panel: cn(
      "border-swdc-silver-200 bg-swdc-white size-full max-h-[90vh] max-w-122 rounded-t-[1.25rem] border lg:h-auto lg:max-h-160 lg:rounded-[1.25rem] lg:[@media(min-height:980px)]:max-h-220",
    ),
    content: cn(
      "mr-1 overflow-auto py-8 pr-3 pl-4 focus:outline-none focus-visible:outline-none lg:py-20 lg:pr-7 lg:pl-8",
    ),
  },
  exitIntent: {
    panelWrapper: cn("fixed inset-0 flex w-screen items-center justify-center"),
    panel: cn(
      "border-swdc-silver-200 bg-swdc-white size-full h-auto max-w-[352px] rounded-[1.25rem] border sm:max-w-[582px]",
    ),
    content: cn(
      "mr-1 overflow-auto p-8 focus:outline-none focus-visible:outline-none lg:pb-10",
    ),
  },
};

const Modal = ({
  as,
  autoFocus = true,
  children,
  closeAnalytics,
  onClose,
  open,
  role,
  unmount,
  variant = "standard",
  className,
}: PropsWithChildren<ModalProps>) => {
  return (
    <Dialog
      as={as}
      autoFocus={autoFocus}
      className="relative z-60"
      onClose={onClose}
      open={open}
      role={role}
      unmount={unmount}
      {...getDataAutomationGroup("alert")}
    >
      <DialogBackdrop
        aria-hidden
        transition
        className="bg-swdc-black/20 fixed inset-0 backdrop-blur-sm transition-[opacity,backdrop-filter] duration-300 data-closed:opacity-10 data-closed:backdrop-blur-[1px]"
      />
      <div className={VARIANTS[variant].panelWrapper}>
        <DialogPanel
          {...getDataAutomationGroup("modal")}
          transition
          className={cn(
            "relative flex overflow-hidden",
            VARIANTS[variant].panel,
            ANIMATION,
            className,
          )}
        >
          <CloseButton
            data-autofocus
            onClick={onClose}
            data-automation-id="close-btn"
            {...getDataLinkAttributes(closeAnalytics)}
          />
          <div
            className={cn("modal-scrollbar w-full", VARIANTS[variant].content)}
          >
            {children}
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

export default Modal;
